/**
 * ============================================================
 * AdminProjects.js — Manage Projects (Admin Panel)
 * ============================================================
 *
 * Loads:
 *   - List of all projects (GET /api/projects)
 *
 * Features:
 *   - Search by project title
 *   - Click a row to edit/delete a project
 *   - "Add Project" button -> /admin/project/new
 *
 * Navigation:
 *   - Uses SynlabAdminNavbar for consistent admin navigation
 *   - Uses shared AdminSubpage.css for layout
 **/

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import SearchBar from "../components/SearchBar";
import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";


// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminProjects() {

  // Component state
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Load projects once token is valid
  // ------------------------------------------------------------
  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await authFetch("/api/projects");
        if (!res) return; // redirected on invalid token

        const data = await res.json();

        // Make sure data is an array
        if (Array.isArray(data)) {
          setProjects(data);
        } else {
          console.warn("Projects API returned non-array: ", data);
          setProjects([]); // fallback to empty list
        }

      } catch (err) {
        console.error("AdminProjects fetch error:", err);
        setProjects([]); // fallback instead of crashing
      }
    }

    fetchProjects();
  }, []);

  // ------------------------------------------------------------
  // Filtered list of projects
  // ------------------------------------------------------------
  const filteredProjects = useMemo(() => {
    const s = search.toLowerCase();

    // Check that projects is an array
    const list = Array.isArray(projects) ? projects : [];    

    return list.filter((p) =>
      (p.name || "").toLowerCase().includes(s)
    );
  }, [search, projects]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- PAGE HEADER ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">Manage Projects</h1>

          <Button
            className="admin-add-button"
            onClick={() => navigate("/admin/projects/new")}
          >
            + Add Project
          </Button>
        </div>

        {/* ----- PAGE DESCRIPTION ----- */}
        <p className="admin-page-description">
          Search, view, and edit all Synlab project records.
        </p>

        {/* ----- SEARCH BAR ----- */}
        <div className="admin-searchbar-wrapper">
          <SearchBar
            placeholder="Search by project title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ----- PROJECTS TABLE IS EMPTY ----- */}
        {projects.length === 0 && search === "" && (
          <div className="admin-empty-state">
            <p>No projects found.</p>
          </div>
        )}

        {/* ----- PROJECTS TABLE ----- */}
        {projects.length > 0 && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Start Year</th>
                  <th>End Year</th>
                </tr>
              </thead>

              <tbody>
                {(search ? filteredProjects : projects).map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => navigate(`/admin/projects/${project.id}`)}
                    className="admin-table-row"
                  >
                    <td>{project.name}</td>
                    <td>{project.startyear}</td>
                    <td>{project.endyear || "Current"}</td>
                  </tr>
                ))}

                {(search && filteredProjects.length === 0) && (
                  <tr>
                    <td colSpan="3">No projects match your search</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* ----- FOOTER ----- */}
      <SynlabAdminFooter />

    </div>
  );
}

export default AdminProjects;

